import { DestroyRef, inject, Signal } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { BackNavigationService } from './back-navigation.service';
import { BackNavigable, NavigateBackOptions } from './back-navigable';
import { queryParam } from '../../functions/activated-route.function';
import { base64Decode, isDefinedNotEmpty } from '@softline/core';

export interface BackNavigationOptions {
  canNavigateBack?: Signal<boolean>;
  ignoreOrigin?: boolean;
}

export function backNavigation(): void;
export function backNavigation(
  route: string | string[],
  options?: BackNavigationOptions
): void;
export function backNavigation(
  routeFn: () => string | string[],
  options?: BackNavigationOptions
): void;
export function backNavigation(
  callback: (options?: NavigateBackOptions) => Promise<void>,
  options?: BackNavigationOptions
): void;
export function backNavigation(backNavigable: BackNavigable): void;
export function backNavigation(
  navigation?:
    | string
    | string[]
    | (() => string | string[])
    | ((options?: NavigateBackOptions) => Promise<void>)
    | BackNavigable,
  options?: BackNavigationOptions
): void {
  const router = inject(Router);
  const destroyRef = inject(DestroyRef);
  const backNavigationService = inject(BackNavigationService);
  const origin = queryParam('origin')();

  let backNavigable: BackNavigable;

  if (isDefinedNotEmpty(origin) && !options?.ignoreOrigin) {
    backNavigable = {
      canNavigateBack: options?.canNavigateBack,
      navigateBack: async (options?: NavigateBackOptions) => {
        await router.navigateByUrl(options?.savepoint ?? base64Decode(origin), {
          state: {
            navigateBack: true,
          },
        });
      },
    };
  } else if (!navigation || typeof navigation === 'string' || Array.isArray(navigation))
    backNavigable = {
      canNavigateBack: options?.canNavigateBack,
      navigateBack: async (options?: NavigateBackOptions) => {
        if (options?.savepoint)
          await router.navigateByUrl(options.savepoint, {
            state: {
              navigateBack: true,
            },
          });
        else if (Array.isArray(navigation))
          await router.navigate(navigation, {
            state: {
              navigateBack: true,
            },
          });
        else
          await router.navigate([navigation ?? '/'], {
            state: {
              navigateBack: true,
            },
          });
      },
    };
  else if (typeof navigation === 'function') {
    backNavigable = {
      canNavigateBack: options?.canNavigateBack,
      navigateBack: async (options?: NavigateBackOptions) => {
        const route = navigation(options);
        if (
          typeof route === 'string' ||
          (typeof route === 'object' && !(route instanceof Promise))
        )
          if (options?.savepoint)
            await router.navigateByUrl(options.savepoint, {
              state: {
                navigateBack: true,
              },
            });
          else if (Array.isArray(route))
            await router.navigate(route, {
              state: {
                navigateBack: true,
              },
            });
          else
            await router.navigate([route], {
              state: {
                navigateBack: true,
              },
            });
        else await route;
      },
    };
  } else backNavigable = navigation;

  backNavigationService.addNode(backNavigable);
  destroyRef.onDestroy(() => backNavigationService.removeNode(backNavigable));
}
